// This hook loads the POD Inline script and tracks iframe-backed previews in the portal.

// Use React tools we need for this hook.
import { useEffect, useMemo, useRef, useState } from "react";

// Reuse the shared external script loader so POD Inline follows the same pattern as other modules.
import { useExternalScript } from "./useExternalScript";

// Import the stored app config getter.
import { GetAppConfig } from "global/config/AppConfigStore";

// Reuse one stable error message when the loaded script does not expose the split preview API we need.
const PREVIEW_API_MISSING_ERROR = "POD Inline split preview API not available on window.";

// Reuse one stable error message when the forced cache-busted reload fails to load at all.
const PREVIEW_API_RELOAD_ERROR = "Could not reload the inline store preview script.";

// Reuse one stable error message when async helper wiring fails after the base script has loaded.
const PREVIEW_API_HELPERS_ERROR = "Could not load the inline store preview helpers.";

// Give the app one simple way to build and track POD Inline previews.
export function usePodInline({ enabled = false, alias = "", settings = [], previewIdentitySettings = null, } = {}) {

    // Preview mode needs the iframe API so split checkout previews can be detected and reloaded when stale.
    const hasRequiredPreviewApi = (api) => {

        // Stop when the API is missing or does not have the required methods.
        return typeof api?.createPreviewIframeDocument === "function" && api?.supportsSplitCheckoutPreview === true;

    };

    // Read the loaded runtime config only when this hook is called.
    const appConfig = GetAppConfig();

    // Get the POD Inline values from config.
    const podInlineUrl = appConfig?.podInlineUrl || "";

    // Get the placeholder image URL for the POD Inline preview iframe.
    const podInlinePlaceholderImageUrl = appConfig?.podInlinePlaceholderImageUrl || "";

    // Keep the embed URL loaded in the parent page so this hook follows the same external module pattern.
    const scriptStatus = useExternalScript(podInlineUrl);

    // Keep a handle to the iframe so we can match postMessage events to the active preview.
    const previewFrameRef = useRef(null);

    // Keep one stable cache-busting token for iframe-driven POD Inline module loads on this page visit.
    const previewScriptCacheBustRef = useRef(Date.now());

    // Track whether we have already forced one cache-busted script refresh for this page load.
    const hasReloadedPreviewApiRef = useRef(false);

    // Track the iframe-level preview state separately from the parent script state.
    const [previewStatus, setPreviewStatus] = useState(enabled ? "loading" : "idle");

    // Track the iframe-level preview error messages separately from the parent script state.
    const [previewError, setPreviewError] = useState("");

    // Track the resolved public POD Inline preview API from the loaded external script.
    const [podInlinePreviewApi, setPodInlinePreviewApi] = useState(() => window.strich?.pod_inline || null);

    // Track whether we are reloading the script to pick up a newer preview API.
    const [previewApiIsReloading, setPreviewApiIsReloading] = useState(false);

    // Track reload-specific failures separately from iframe rendering failures.
    const [previewApiError, setPreviewApiError] = useState("");

    // Some embed builds install the preview API asynchronously from a sibling helper script.
    const previewReadyPromiseRef = useRef(null);
    
    // Keep the latest settings available so the next forced rebuild can use them.
    const latestSettingsRef = useRef(settings);

    // Prefer the explicit preview identity settings when provided, but fall back to the currently editing draft settings.
    const resolvedPreviewIdentitySettings = previewIdentitySettings ?? settings;

    // Always keep the latest draft settings available so the next forced rebuild can use them.
    useEffect(() => {
        
        // Keep the latest settings available so the next forced rebuild can use them.
        latestSettingsRef.current = settings;

    }, [settings]);

    // Keep one stable ID per preview so postMessage events can be matched to the latest iframe.
    const previewKey = useMemo(() => {

        // Stop when there is nothing to preview yet.
        if (!enabled || !alias) {
            return "";
        }

        // Tie the preview identity to the saved alias and saved settings only.
        return JSON.stringify({
            alias,
            isPreview: true,
            imageUrl: podInlinePlaceholderImageUrl,
            settings: resolvedPreviewIdentitySettings,
        });

    }, [enabled, alias, podInlinePlaceholderImageUrl, resolvedPreviewIdentitySettings]);

    // Keep the last committed iframe document settings stable until the preview identity changes.
    const [previewDocumentSettings, setPreviewDocumentSettings] = useState(settings);

    // Freeze the iframe document input until one of the explicit rebuild settings changes.
    useEffect(() => {

        // Update the preview document settings with the latest settings.
        setPreviewDocumentSettings(latestSettingsRef.current);
    
    }, [previewKey]);

    // Give the iframe its own cache-busted POD Inline script URL so child module updates like cart.js are not held back by an older browser cache entry.
    const previewScriptUrl = useMemo(() => {

        // Stop when the script URL is missing from config.
        if (!podInlineUrl) {
            return "";
        }

        // Reuse one stable cache-busting token for this page load.
        const separator = podInlineUrl.includes("?") ? "&" : "?";
    
        // Add one stable cache buster so preview loads do not get stuck on an older browser-cached script.
        return `${podInlineUrl}${separator}portal_preview_iframe=${previewScriptCacheBustRef.current}`;

    }, [podInlineUrl]);

    // Build the iframe document for the current preview state through the external module API.
    const previewSrcDoc = useMemo(() => {

        // Stop when the preview is not active yet.
        if (!enabled || !alias || scriptStatus !== "ready") {
            return "";
        }

        // Stop when the external module has not exposed the preview builder yet.
        if (!hasRequiredPreviewApi(podInlinePreviewApi)) {
            return "";
        }

        try {

            // Ask the external POD Inline module to build the iframe document for us.
            return podInlinePreviewApi.createPreviewIframeDocument({
                previewId: previewKey,
                alias,
                isPreview: true,
                imageUrl: podInlinePlaceholderImageUrl,
                settings: previewDocumentSettings,
                scriptUrl: previewScriptUrl,
                checkoutPreviewLayout: "split",
            });

        } catch (error) {

            // Surface the real preview generation failure so the UI can show a useful reason.
            setPreviewError(
                error?.message
                    ? `POD Inline preview could not be generated: ${error.message}`
                    : "POD Inline preview could not be generated."
            );

            // Return an empty iframe document and let the effect expose the related error state.
            return "";

        }

    }, [
        enabled,
        alias,
        previewKey,
        previewDocumentSettings,
        podInlinePlaceholderImageUrl,
        previewScriptUrl,
        scriptStatus,
        podInlinePreviewApi,
    ]);

    // Resolve the public preview API and force one cache-busted reload when the page still has an older in-memory script.
    useEffect(() => {

        // Track whether this effect has already been cleaned up so async callbacks do not set stale state afterwards.
        let isCancelled = false;

        // Read the current preview API from the shared Strich namespace.
        const readPreviewApi = () => window.strich?.pod_inline || null;

        // Keep the preview API-related state updates in one place.
        const finishResolvingPreviewApi = (api, error = "") => {

            // Stop when this effect has already been cleaned up.
            if (isCancelled) {
                return;
            }

            // Save the resolved preview API, or null when the required API is still unavailable.
            setPodInlinePreviewApi(api);

            // Mark preview API resolution as finished so the UI can stop showing the reload/loading state.
            setPreviewApiIsReloading(false);

            // Save any preview API-specific error so the iframe state effect can surface it to the user.
            setPreviewApiError(error);

        };

        // Wait for helper scripts that finish wiring up the preview API after the main script tag loads.
        const waitForPreviewHelpers = async (previewReadyPromise) => {

            // Skip if we've already handled this promise.
            if (previewReadyPromiseRef.current === previewReadyPromise) {
                return false;
            }

            // Remember this exact promise so we do not attach duplicate handlers for the same helper flow.
            previewReadyPromiseRef.current = previewReadyPromise;

            // Show a loading state while we wait for the helper scripts to finish exposing the full preview API.
            setPreviewApiIsReloading(true);

            // Clear any older API-specific error before we wait for the helper promise to settle.
            setPreviewApiError("");

            try {

                // Wait for the external helper script to finish wiring up the preview API on window.
                await previewReadyPromise;

                // Re-read the preview API from window after the helper promise resolves.
                const resolvedPreviewApi = readPreviewApi();

                // Only expose the resolved API if it now contains the split preview helpers we require.
                finishResolvingPreviewApi(
                    hasRequiredPreviewApi(resolvedPreviewApi) ? resolvedPreviewApi : null,
                    hasRequiredPreviewApi(resolvedPreviewApi) ? "" : PREVIEW_API_MISSING_ERROR
                );

            } catch {

                // Surface a helper-specific error when the async wiring promise rejects.
                finishResolvingPreviewApi(null, PREVIEW_API_HELPERS_ERROR);

            }

            // Tell the caller we handled this helper promise path.
            return true;

        };

        // Force one cache-busted reload when the already-loaded script does not expose the newer preview API.
        const reloadPreviewScript = () => {

            // Remember that we have already attempted the one allowed cache-busted reload for this page load.
            hasReloadedPreviewApiRef.current = true;

            // Show a loading state while the fresh script tag is downloading and executing.
            setPreviewApiIsReloading(true);

            // Clear any older API-specific error before the forced reload starts.
            setPreviewApiError("");

            // Reuse the correct query string separator for URLs that may already contain query params.
            const separator = podInlineUrl.includes("?") ? "&" : "?";

            // Add a one-off cache buster so the browser fetches a fresh copy instead of reusing an older cached build.
            const cacheBustedUrl = `${podInlineUrl}${separator}portal_preview_reload=${Date.now()}`;

            // Create a standalone script tag so we can load one fresh copy without disturbing the shared loader hook.
            const script = document.createElement("script");

            // Point the script tag at the cache-busted URL.
            script.src = cacheBustedUrl;

            // Let the browser load the replacement script asynchronously.
            script.async = true;

            // Re-check the public API after the fresh script finishes loading.
            script.onload = () => {

                // Read the current preview API from window after the forced reload completes.
                const reloadedPreviewApi = readPreviewApi();

                // Only expose the reloaded API if it now includes the split preview helpers we need.
                finishResolvingPreviewApi(
                    hasRequiredPreviewApi(reloadedPreviewApi) ? reloadedPreviewApi : null,
                    hasRequiredPreviewApi(reloadedPreviewApi) ? "" : PREVIEW_API_MISSING_ERROR
                );
            };

            // Surface a specific reload failure if the fresh script tag itself cannot load.
            script.onerror = () => {
                finishResolvingPreviewApi(null, PREVIEW_API_RELOAD_ERROR);
            };

            // Inject the fresh script tag into the page so the browser can fetch and execute it.
            document.body.appendChild(script);

            // Return cleanup so this effect can detach script callbacks when dependencies change or the hook unmounts.
            return () => {

                // Remove the load callback reference so stale async work cannot update state through this old script tag.
                script.onload = null;

                // Remove the error callback reference for the same reason.
                script.onerror = null;
            };

        };

        // Reset API state when the preview URL is missing.
        if (!podInlineUrl) {
            hasReloadedPreviewApiRef.current = false;
            previewReadyPromiseRef.current = null;
            setPodInlinePreviewApi(null);
            setPreviewApiIsReloading(false);
            setPreviewApiError("");
            return;
        }

        // Wait until the shared script loader reports that the base script tag has loaded.
        if (scriptStatus !== "ready") {

            // Stop showing API reload state while we are still waiting on the shared script loader.
            setPreviewApiIsReloading(false);

            // Clear API-specific errors until the shared script loader has actually finished.
            setPreviewApiError("");
            return;
        }

        // Read whichever preview API the current script has placed on window so we can inspect its capabilities.
        const currentPreviewApi = readPreviewApi();

        // Use the already-loaded API when the new preview builder is present.
        if (hasRequiredPreviewApi(currentPreviewApi)) {

            // Save the current API immediately when it already exposes the split preview helpers we need.
            finishResolvingPreviewApi(currentPreviewApi);
            return;
        }

        // Wait for the async helper script to finish wiring up the preview mode API after the main script tag loads.
        const previewReadyPromise = currentPreviewApi?.previewReadyPromise && typeof currentPreviewApi.previewReadyPromise.then === "function" ? currentPreviewApi.previewReadyPromise : null;

        if (previewReadyPromise) {

            // Let the async helper path finish wiring the preview API before we fall back to forcing a reload.
            void waitForPreviewHelpers(previewReadyPromise);
            return () => {

                // Mark this effect as cleaned up so the helper promise cannot update stale state afterwards.
                isCancelled = true;
            };
        }

        // Stop after one forced refresh attempt so the UI can show a stable error instead of looping forever.
        if (hasReloadedPreviewApiRef.current) {
            finishResolvingPreviewApi(null, PREVIEW_API_MISSING_ERROR);
            return;
        }

        const cleanupReloadScript = reloadPreviewScript();

        return () => {

            // Mark this effect as cleaned up so the forced reload callbacks cannot update stale state afterwards.
            isCancelled = true;

            // Run the script callback cleanup returned by the forced reload helper.
            cleanupReloadScript?.();
        };

    }, [podInlineUrl, scriptStatus]);

    // Track iframe loading and error messages coming back from the preview.
    useEffect(() => {

        // Reset everything while the preview is inactive.
        if (!enabled) {
            setPreviewStatus("idle");
            setPreviewError("");
            return;
        }

        // Surface config problems before the iframe tries to boot.
        if (!podInlineUrl) {
            setPreviewStatus("error");
            setPreviewError("Inline store preview is not configured.");
            return;
        }

        // Surface parent script load failures using the shared loader status.
        if (scriptStatus === "error") {
            setPreviewStatus("error");
            setPreviewError("Could not load the inline store preview script.");
            return;
        }

        // Wait until the shared loader marks the script URL as ready.
        if (scriptStatus !== "ready") {
            setPreviewStatus("loading");
            setPreviewError("");
            return;
        }

        // Wait while we refresh the script to pick up a newer preview API.
        if (previewApiIsReloading) {
            setPreviewStatus("loading");
            setPreviewError("");
            return;
        }

        // Surface reload-specific failures separately from iframe rendering failures.
        if (previewApiError) {
            setPreviewStatus("error");
            setPreviewError(previewApiError);
            return;
        }

        // Stop when the expected public preview builder is not on window yet.
        if (!hasRequiredPreviewApi(podInlinePreviewApi)) {
            setPreviewStatus("error");
            setPreviewError("POD Inline split preview API not available on window.");
            return;
        }

        // Stop when the iframe document could not be created, even though the alias is present.
        if (!previewSrcDoc) {
            setPreviewStatus("error");
            setPreviewError(
                alias
                    ? "POD Inline preview could not be generated."
                    : "POD Inline preview requires a saved store alias."
            );
            return;
        }

        if (!previewKey) {
            setPreviewStatus("error");
            setPreviewError("POD Inline preview requires a saved store alias.");
            return;
        }

        // Start in a loading state until the iframe reports back.
        setPreviewStatus("loading");
        setPreviewError("");

        const timeoutId = window.setTimeout(() => {
            setPreviewStatus("error");
            setPreviewError("Inline store preview timed out.");
        }, 30000);

        // Match the message channel used by the iframe preview build, with a fallback for older script versions.
        const previewMessageType =
            podInlinePreviewApi?.previewMessageType || "strich-pod-inline-preview-status";


        // Handle postMessage events from the preview iframe and update the parent state accordingly.
        const handlePreviewMessage = (event) => {

            // Only trust messages from the active preview frame.
            if (event.source !== previewFrameRef.current?.contentWindow) {
                return;
            }

            // set the message data to a variable for easier access.
            const message = event.data;

            // Ignore unrelated postMessage traffic.
            if (message?.type !== previewMessageType) {

                // Ignore unrelated postMessage traffic.
                return;
            
            }

            // Ignore stale messages from an older preview instance.
            if (message.previewId !== previewKey) {
            
                // Ignore stale messages from an older preview instance.
                return;

            }

            // Keep loading until the iframe reports either ready or error.
            if (message.status === "loading") {
                
                // Keep loading until the iframe reports either ready or error.
                setPreviewStatus("loading");

                // Clear any previous error so the UI can show a loading state.
                setPreviewError("");

                // Stop here and wait for the next message.
                return;
            }

            // Stop the timeout and update the parent state when the iframe reports ready or error.
            if (message.status === "ready") {

                // Stop the timeout and update the parent state when the iframe reports ready or error.
                window.clearTimeout(timeoutId);

                // Mark the preview as ready and clear any previous error so the UI can show a ready state.
                setPreviewStatus("ready");

                // Clear any previous error so the UI can show a ready state.
                setPreviewError("");

                // Stop here and wait for the next message.
                return;
            }

            // Stop the timeout and update 
            if (message.status === "error") {
                
                // Stop the timeout 
                window.clearTimeout(timeoutId);
                
                // Update parent state
                setPreviewStatus("error");

                // Show the error message from the iframe, or a generic message if none is provided.
                setPreviewError(message.message || "Could not load the inline store preview.");

            }

        };

        // Register the postMessage listener so the parent can track the iframe preview state.
        window.addEventListener("message", handlePreviewMessage);

        // Clean up listeners and timers when the preview changes (so we don't get dupe events) or when this hook instance unmounts.
        return () => {
            // Stop the timeout
            window.clearTimeout(timeoutId);

            // Remove the postMessage listener  
            window.removeEventListener("message", handlePreviewMessage);
        };

    }, [
        alias,
        enabled,
        podInlinePreviewApi,
        podInlineUrl,
        previewApiError,
        previewApiIsReloading,
        previewKey,
        previewSrcDoc,
        scriptStatus,
    ]);

    // Expose the preview state in the same simple style as other external module hooks.
    return {

        // Main way for the popup to render the POD Inline preview iframe.
        previewFrameRef,

        // The iframe HTML document for the current preview.
        previewSrcDoc,

        // Keep one stable key per preview document.
        previewKey,

        // True while the parent script or iframe preview is still loading.
        isLoading: scriptStatus === "loading" || previewApiIsReloading || previewStatus === "loading",

        // True when the iframe preview is ready to display.
        isReady: previewStatus === "ready",

        // The current iframe-level error text, if any.
        error: previewError,

        // Expose the preview status as well.
        status: previewStatus,

        // Expose the message type so callers can listen for child preview actions.
        previewMessageType: podInlinePreviewApi?.previewMessageType || "strich-pod-inline-preview-status",

    };

}
